import { useState, useEffect, useCallback } from 'react';

export interface UseLocalStorageOptions {
  serializer?: (value: any) => string;
  deserializer?: (value: string) => any;
  onError?: (error: Error) => void;
}

const defaultSerializer = JSON.stringify;
const defaultDeserializer = JSON.parse;

export const useLocalStorage = <T>(
  key: string,
  initialValue: T,
  options: UseLocalStorageOptions = {}
): [T, (value: T | ((val: T) => T)) => void, () => void] => {
  const {
    serializer = defaultSerializer,
    deserializer = defaultDeserializer,
    onError = console.error,
  } = options;

  // Get initial value from localStorage or use provided initial value
  const getStoredValue = useCallback((): T => {
    try {
      const item = window.localStorage.getItem(key);
      if (item !== null) {
        return deserializer(item);
      }
    } catch (error) {
      onError(error as Error);
    }
    return initialValue;
  }, [key, initialValue, deserializer, onError]);

  const [storedValue, setStoredValue] = useState<T>(getStoredValue);

  // Update localStorage when storedValue changes
  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      try {
        // Allow value to be a function so we have the same API as useState
        const valueToStore = value instanceof Function ? value(storedValue) : value;
        
        // Save to state
        setStoredValue(valueToStore);
        
        // Save to localStorage
        window.localStorage.setItem(key, serializer(valueToStore));
      } catch (error) {
        onError(error as Error);
      }
    },
    [key, storedValue, serializer, onError]
  );

  // Remove value from localStorage
  const removeValue = useCallback(() => {
    try {
      setStoredValue(initialValue);
      window.localStorage.removeItem(key);
    } catch (error) {
      onError(error as Error);
    }
  }, [key, initialValue, onError]);

  // Listen for changes to localStorage from other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(deserializer(e.newValue));
        } catch (error) {
          onError(error as Error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key, deserializer, onError]);

  return [storedValue, setValue, removeValue];
};

// Hook for managing multiple localStorage values
export const useLocalStorageMulti = <T extends Record<string, any>>(
  keys: (keyof T)[],
  initialValues: T,
  options: UseLocalStorageOptions = {}
): [T, (updates: Partial<T>) => void, (keysToRemove: (keyof T)[]) => void] => {
  const [values, setValues] = useState<T>(() => {
    const storedValues = { ...initialValues };
    
    keys.forEach(key => {
      try {
        const item = window.localStorage.getItem(String(key));
        if (item !== null) {
          storedValues[key] = options.deserializer 
            ? options.deserializer(item)
            : JSON.parse(item);
        }
      } catch (error) {
        options.onError?.(error as Error);
      }
    });
    
    return storedValues;
  });

  const updateValues = useCallback(
    (updates: Partial<T>) => {
      const newValues = { ...values, ...updates };
      setValues(newValues);
      
      // Update localStorage for each changed key
      Object.entries(updates).forEach(([key, value]) => {
        try {
          const serializedValue = options.serializer 
            ? options.serializer(value)
            : JSON.stringify(value);
          window.localStorage.setItem(key, serializedValue);
        } catch (error) {
          options.onError?.(error as Error);
        }
      });
    },
    [values, options]
  );

  const removeValues = useCallback(
    (keysToRemove: (keyof T)[]) => {
      const newValues = { ...values };
      keysToRemove.forEach(key => {
        delete newValues[key];
        try {
          window.localStorage.removeItem(String(key));
        } catch (error) {
          options.onError?.(error as Error);
        }
      });
      setValues(newValues);
    },
    [values, options]
  );

  return [values, updateValues, removeValues];
};
